import * as amplitude from '@amplitude/analytics-browser';
import airbridge from 'airbridge-web-sdk-loader';

export interface DesktopTrackingInitOptions {
  amplitudeApiKey: string;
  appVersion?: string;
  airbridge: {
    app: string;
    webToken: string;
  };
}

const DEFAULT_READY_TIMEOUT_MS = 5000;
const DEVICE_ID_MAX_ATTEMPTS = 10;
const DEVICE_ID_INTERVAL_MS = 500;

let _resolveReady: (() => void) | null = null;
let _readyPromise: Promise<void> | null = null;
let _initStarted = false;

function _ensureReadyPromise(): Promise<void> {
  if (!_readyPromise) {
    _readyPromise = new Promise<void>((resolve) => {
      _resolveReady = resolve;
    });
  }
  return _readyPromise;
}

function _signalTrackingReady(): void {
  _ensureReadyPromise();
  _resolveReady?.();
  _resolveReady = null;
}

async function _waitForAmplitudeDeviceId(
  maxAttempts = DEVICE_ID_MAX_ATTEMPTS,
  intervalMs = DEVICE_ID_INTERVAL_MS
): Promise<string | undefined> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const deviceId = amplitude.getDeviceId();
    if (deviceId) {
      return deviceId;
    }
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  return undefined;
}

/**
 * Initializes Amplitude then Airbridge (alias + startTracking) and signals tracking readiness.
 * Safe to call once from desktop bootstrap; subsequent calls are no-ops.
 */
export function initDesktopTracking(options: DesktopTrackingInitOptions): void {
  if (_initStarted) {
    return;
  }
  _initStarted = true;
  _ensureReadyPromise();

  amplitude.init(options.amplitudeApiKey, {
    autocapture: {
      attribution: true,
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
    appVersion: options.appVersion,
  });

  airbridge.init({
    app: options.airbridge.app,
    webToken: options.airbridge.webToken,
    autoStartTrackingEnabled: false,
  });

  void (async () => {
    try {
      const deviceId = await _waitForAmplitudeDeviceId();
      if (deviceId) {
        airbridge.setDeviceAlias('amplitude_device_id', deviceId);
      }
      airbridge.startTracking();
    } catch {
      // Best-effort linking; readiness must still resolve so callers are not stuck.
    } finally {
      _signalTrackingReady();
    }
  })();
}

/**
 * Resolves when desktop tracking has finished starting, or when `timeoutMs` elapses.
 * Never rejects — SSO login can proceed after the timeout.
 */
export function waitForTrackingReady(timeoutMs = DEFAULT_READY_TIMEOUT_MS): Promise<void> {
  return Promise.race([
    _ensureReadyPromise(),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

/** @internal Resets module latch state for unit tests. */
export function resetTrackingReadyForTests(): void {
  _readyPromise = null;
  _resolveReady = null;
  _initStarted = false;
}
